import { useState, useMemo, useEffect } from 'react';
import { isOnboardingDisabled } from 'common/env';
import { OnboardingFeatureHighlights } from 'components/onboarding-feature-highlights/onboarding-feature-highlights.component';
import { OnboardingAccountSetup } from 'components/onboarding-account-setup/onboarding-account-setup.component';
import { useOnboardingCompletion } from '../use-onboarding-completion/use-onboarding-completion.hook';

export const useOnboarding = () => {
  const {
    accountSetupComplete,
    featureHighlightsComplete,
    isLoading: onboardingIsLoading,
    error,
    stepFlagsKey,
  } = useOnboardingCompletion();

  // Track whether we've initialized the steps based on onboardingComplete
  const [isInitialized, setIsInitialized] = useState(false);

  // Assume that the steps are completed by default
  const [stepsCompleted, setStepsCompleted] = useState({
    featureHighlights: true,
    accountSetup: true,
  });

  useEffect(() => {
    // Wait until loading is complete before initializing
    if (onboardingIsLoading) return;

    if (isOnboardingDisabled()) {
      setStepsCompleted({
        featureHighlights: true,
        accountSetup: true,
      });
    } else {
      setStepsCompleted({
        featureHighlights: featureHighlightsComplete,
        accountSetup: accountSetupComplete,
      });
    }

    setIsInitialized(true);
  }, [onboardingIsLoading, featureHighlightsComplete, accountSetupComplete]);

  const steps = useMemo(
    () => [
      {
        isComplete: stepsCompleted.featureHighlights,
        component: (
          <OnboardingFeatureHighlights
            onOnboardingComplete={async () => {
              setStepsCompleted((prev) => ({
                ...prev,
                featureHighlights: true,
              }));
            }}
          />
        ),
      },
      {
        isComplete: stepsCompleted.accountSetup,
        component: (
          <OnboardingAccountSetup
            key={stepFlagsKey}
            onOnboardingComplete={async () => {
              setStepsCompleted((prev) => ({
                ...prev,
                accountSetup: true,
              }));
            }}
          />
        ),
      },
    ],
    [stepsCompleted, stepFlagsKey],
  );

  const currentStep = useMemo(
    () => steps.find((step) => !step.isComplete),
    [steps],
  );

  // If all steps are complete, set isOnboardingComplete to true
  const isOnboardingComplete = !currentStep;

  return {
    currentStep,
    isOnboardingComplete,
    isLoading: onboardingIsLoading || !isInitialized,
    error,
  };
};
